import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

interface WeldingEquipmentDetail {
  name: string;
  spec: string;
  description: string;
  icon: string;
  badge: string;
  imageUrl: string;
  detailTitle: string;
  detailDesc: string;
  features: string[];
  applications: string[];
  specTable: { label: string; value: string }[];
}

export default function WeldingEquipmentPage() {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<'mig' | 'tig' | 'co2' | 'laser'>('mig');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const navLinks = [
    { href: '/#about', label: '회사소개', type: 'dropdown', subMenu: [
      { href: '/#about', label: '회사소개' },
      { href: '/history', label: '연혁' },
      { href: '/organization', label: '조직도' },
      { href: null, label: '오시는 길', scrollTo: 'map' },
    ]},
    { href: '/#process', label: '생산공정', type: 'scroll' },
    { href: '#equipment', label: '보유장비', type: 'dropdown', subMenu: [
      { href: '/equipment/production', label: '생산장비' },
      { href: '/equipment/welding', label: '용접장비' },
      { href: '/equipment/capability', label: '가공능력' },
      { href: '/equipment/materials', label: '가공 가능자재' },
    ]},
    { href: '#products', label: '제조물품', type: 'dropdown', subMenu: [
      { href: '/gallery', label: '갤러리' },
    ]},
  ];

  const handleGoToMap = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('map');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const weldingEquipment: Record<'mig' | 'tig' | 'co2' | 'laser', WeldingEquipmentDetail> = {
    mig: {
      name: 'MIG 용접기',
      spec: 'MIG Welding',
      description: '반자동 용접, 생산성 우수',
      icon: 'ri-fire-line',
      badge: 'bg-orange-500',
      imageUrl: 'https://public.readdy.ai/ai/img_res/63062da67b850dd9c8c5628fc780332d.jpg',
      detailTitle: 'MIG 용접기 (Metal Inert Gas Welding)',
      detailDesc: 'MIG 용접기는 반자동 가스 메탈 아크 용접 장비로, 와이어 전극과 보호 가스를 사용하여 연속적인 용접이 가능합니다. 높은 생산성과 안정적인 용접 품질로 철판 및 스테인리스 제작에 널리 사용됩니다.',
      features: [
        '용접 전류 범위 200-350A',
        '와이어 자동 공급 시스템',
        '철판·스테인리스 용접 대응',
        '연속 용접 가능, 생산성 우수',
        '초보자도 쉽게 사용 가능',
        '아르곤·CO2 혼합 가스 사용',
      ],
      applications: ['철판 구조물 제작', '스테인리스 제품 용접', '자동차 부품 용접', '대량 생산 라인'],
      specTable: [
        { label: '용접 전류', value: '200-350A' },
        { label: '용접 방식', value: 'MIG (반자동)' },
        { label: '보호 가스', value: 'Ar + CO₂ 혼합' },
        { label: '와이어 직경', value: '0.8-1.2mm' },
        { label: '적용 소재', value: '철판, 스테인리스' },
        { label: '최대 두께', value: '12mm' },
      ],
    },
    tig: {
      name: 'TIG 용접기',
      spec: 'TIG Welding',
      description: '정밀 용접, 스테인리스 특화',
      icon: 'ri-flashlight-line',
      badge: 'bg-yellow-500',
      imageUrl: 'https://readdy.ai/api/search-image?query=TIG%20welding%20machine%20precision%20argon%20arc%20welder%20stainless%20steel%20professional%20industrial%20equipment%20workshop%20clean%20neat%20setup%20high%20resolution%20photography&width=800&height=500&seq=weld-tig-hero-001&orientation=landscape',
      detailTitle: 'TIG 용접기 (Tungsten Inert Gas Welding)',
      detailDesc: 'TIG 용접기는 텅스텐 전극과 불활성 가스를 사용하는 고정밀 용접 장비입니다. 스테인리스, 알루미늄 등 다양한 소재에 깨끗하고 정밀한 용접이 가능하며, 외관이 중요한 제품 제작에 최적화되어 있습니다.',
      features: [
        '용접 전류 5-300A 정밀 제어',
        '텅스텐 전극 사용, 오염 최소화',
        '스테인리스·알루미늄 특화',
        '깨끗한 용접 비드 형성',
        '얇은 판재 정밀 용접 가능',
        '아르곤 가스 보호',
      ],
      applications: ['스테인리스 정밀 제품', '알루미늄 부품 용접', '식품 기계 제작', '의료 기기 부품'],
      specTable: [
        { label: '용접 전류', value: '5-300A' },
        { label: '용접 방식', value: 'TIG (수동)' },
        { label: '보호 가스', value: '아르곤 (Ar)' },
        { label: '전극', value: '텅스텐 (W)' },
        { label: '적용 소재', value: 'STS, 알루미늄' },
        { label: '최소 두께', value: '0.5mm' },
      ],
    },
    co2: {
      name: 'CO2 용접기',
      spec: 'CO2 Welding',
      description: '철판 용접, 경제적 효율',
      icon: 'ri-bubble-chart-line',
      badge: 'bg-gray-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=CO2%20welding%20machine%20carbon%20dioxide%20arc%20welder%20industrial%20steel%20fabrication%20workshop%20equipment%20professional%20manufacturing%20facility%20photography&width=800&height=500&seq=weld-co2-hero-001&orientation=landscape',
      detailTitle: 'CO2 용접기 (Carbon Dioxide Arc Welding)',
      detailDesc: 'CO2 용접기는 탄산가스를 보호 가스로 사용하는 경제적인 용접 장비입니다. 철판 용접에 특화되어 있으며, 낮은 운영 비용과 높은 용접 속도로 대량 생산에 적합합니다.',
      features: [
        '용접 전류 범위 150-400A',
        'CO2 가스 사용, 경제적',
        '철판 용접에 최적화',
        '높은 용접 속도',
        '두꺼운 철판 용접 가능',
        '대량 생산에 적합',
      ],
      applications: ['철판 구조물 제작', '건축 철골 용접', '중장비 부품 제작', '대형 철판 가공'],
      specTable: [
        { label: '용접 전류', value: '150-400A' },
        { label: '용접 방식', value: 'CO₂ (반자동)' },
        { label: '보호 가스', value: 'CO₂ (탄산가스)' },
        { label: '와이어 직경', value: '1.0-1.6mm' },
        { label: '적용 소재', value: '철판 (연강)' },
        { label: '최대 두께', value: '25mm' },
      ],
    },
    laser: {
      name: '레이저 용접기',
      spec: 'Laser Welding',
      description: '고정밀 고강도 레이저 용접',
      icon: 'ri-flashlight-fill',
      badge: 'bg-orange-500',
      imageUrl: 'https://readdy.ai/api/search-image?query=industrial%20laser%20welding%20machine%20close%20up%20bright%20laser%20beam%20welding%20stainless%20steel%20metal%20parts%20in%20dark%20factory%20workshop%20with%20intense%20glowing%20light%20and%20sparks%20professional%20manufacturing%20equipment%20high%20resolution%20photography&width=800&height=500&seq=weld-laser-hero-001&orientation=landscape',
      detailTitle: '레이저 용접기 (Laser Beam Welding)',
      detailDesc: '레이저 용접기는 고출력 레이저 빔을 이용한 최첨단 용접 장비입니다. 극도로 정밀한 용접이 가능하며, 열변형이 최소화되어 고강도·고품질 용접이 요구되는 정밀 부품 제작에 사용됩니다.',
      features: [
        '최대 출력 3,000W 레이저',
        '용접 정밀도 ±0.05mm 이하',
        '열변형 최소화',
        '고속 용접 가능',
        '스테인리스·알루미늄·철판 대응',
        '복잡한 형상 용접 가능',
      ],
      applications: ['정밀 전자 부품', '의료 기기 제작', '자동차 부품 용접', '고강도 구조물'],
      specTable: [
        { label: '레이저 출력', value: '최대 3,000W' },
        { label: '용접 방식', value: '레이저 빔 용접' },
        { label: '정밀도', value: '±0.05mm' },
        { label: '용접 속도', value: '최대 10m/min' },
        { label: '적용 소재', value: 'STS, 알루미늄, 철판' },
        { label: '최대 두께', value: '6mm' },
      ],
    },
  };

  const materialRanges = [
    {
      icon: 'ri-stack-line',
      title: '철판 (연강)',
      thickness: '1.0 ~ 25mm',
      methods: ['MIG', 'CO₂', '레이저'],
      color: 'bg-gray-100 border-gray-200',
      iconBg: 'bg-gray-600',
    },
    {
      icon: 'ri-contrast-2-line',
      title: '스테인리스 (STS)',
      thickness: '0.5 ~ 12mm',
      methods: ['TIG', 'MIG', '레이저'],
      color: 'bg-blue-50 border-blue-200',
      iconBg: 'bg-blue-500',
    },
    {
      icon: 'ri-box-3-line',
      title: '알루미늄 (AL)',
      thickness: '0.8 ~ 8mm',
      methods: ['TIG', '레이저'],
      color: 'bg-purple-50 border-purple-200',
      iconBg: 'bg-purple-500',
    },
    {
      icon: 'ri-git-merge-line',
      title: '이종소재 용접',
      thickness: '맞춤 제작',
      methods: ['레이저', 'TIG'],
      color: 'bg-orange-50 border-orange-200',
      iconBg: 'bg-orange-500',
    },
  ];

  const processSteps = [
    { icon: 'ri-pencil-ruler-2-line', title: 'CAD 설계', desc: '도면 작성 및 검토' },
    { icon: 'ri-scissors-cut-line', title: '레이저 절단', desc: '정밀 소재 절단' },
    { icon: 'ri-tools-line', title: '절곡 가공', desc: '벤딩 및 성형' },
    { icon: 'ri-fire-line', title: '용접 작업', desc: 'MIG/TIG/CO₂/레이저' },
    { icon: 'ri-checkbox-circle-line', title: '검수 및 납품', desc: '품질 검사 후 출고' },
  ];

  const currentEquipment = weldingEquipment[selectedTab];

  return (
    <>
      <SEOHead
        title="용접장비 | NS-TECH 판금가공"
        description="NS-TECH의 MIG, TIG, CO₂, 레이저 용접 전 라인. 다양한 소재와 두께에 최적화된 용접 솔루션으로 고품질 용접 서비스를 제공합니다."
        keywords="MIG 용접, TIG 용접, CO2 용접, 레이저 용접, 용접 장비, 금속 용접, 스테인리스 용접"
        ogType="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'NS-TECH 용접장비',
            description: 'MIG·TIG·CO₂·레이저 용접 전 라인 구축',
            url: `${import.meta.env.VITE_SITE_URL}/equipment/welding`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                item: {
                  '@type': 'Product',
                  name: 'MIG 용접기',
                  description: '반자동 용접, 생산성 우수',
                },
              },
              {
                '@type': 'ListItem',
                position: 2,
                item: {
                  '@type': 'Product',
                  name: 'TIG 용접기',
                  description: '정밀 용접, 스테인리스 특화',
                },
              },
              {
                '@type': 'ListItem',
                position: 3,
                item: {
                  '@type': 'Product',
                  name: 'CO2 용접기',
                  description: '철판 용접, 경제적 효율',
                },
              },
              {
                '@type': 'ListItem',
                position: 4,
                item: {
                  '@type': 'Product',
                  name: '레이저 용접기',
                  description: '고정밀 고강도 레이저 용접',
                },
              },
            ],
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        {/* 네비게이션 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
          <div className="w-full px-4 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/" className="flex items-center gap-2 shrink-0 mr-2" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
                  <img src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png" alt="NS-TECH Logo" className="h-12 w-auto object-contain" />
                </Link>
                {navLinks.map((link) => (
                  link.type === 'dropdown' ? (
                    <div key={link.label} className="relative"
                      onMouseEnter={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(true);
                        if (link.label === '제조물품') setProductsDropdownOpen(true);
                        if (link.label === '회사소개') setAboutDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(false);
                        if (link.label === '제조물품') setProductsDropdownOpen(false);
                        if (link.label === '회사소개') setAboutDropdownOpen(false);
                      }}
                    >
                      <button className={`text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md flex items-center gap-1 cursor-pointer ${
                        link.label === '보유장비' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                      }`}>
                        {link.label}
                        <i className={`ri-arrow-down-s-line transition-transform ${
                          (link.label === '보유장비' && equipmentDropdownOpen) ||
                          (link.label === '제조물품' && productsDropdownOpen) ||
                          (link.label === '회사소개' && aboutDropdownOpen) ? 'rotate-180' : ''
                        }`}></i>
                      </button>
                      {((link.label === '보유장비' && equipmentDropdownOpen) ||
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)) && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                          {link.subMenu?.map((subLink) => (
                            subLink.scrollTo === 'map' ? (
                              <button
                                key={subLink.label}
                                onClick={handleGoToMap}
                                className="block w-full text-left px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                              >
                                {subLink.label}
                              </button>
                            ) : (
                              <Link
                                key={subLink.href}
                                to={subLink.href!}
                                className={`block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap ${
                                  subLink.href === '/equipment/welding' ? 'text-orange-500 font-semibold' : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link key={link.href} to={link.href!} className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                      {link.label}
                    </Link>
                  )
                ))}
              </div>
              <div className="flex lg:hidden items-center justify-between w-full">
                <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}><img src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png" alt="NS-TECH Logo" className="h-10 w-auto object-contain" /></Link>
                <button
                  className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="메뉴 열기"
                >
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </button>
              </div>
              <div className="hidden lg:flex items-center">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                >
                  고객지원
                </button>
              </div>
            </div>
          </div>
          <div className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)}></div>
          <div className={`lg:hidden fixed top-[60px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
            <div className="bg-white shadow-xl border-t border-gray-100 px-5 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                link.type === 'dropdown' ? (
                  <div key={link.label}>
                    <button onClick={() => {
                      if (link.label === '보유장비') setMobileEquipmentOpen(!mobileEquipmentOpen);
                      if (link.label === '제조물품') setMobileProductsOpen(!mobileProductsOpen);
                      if (link.label === '회사소개') setMobileAboutOpen(!mobileAboutOpen);
                    }} className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between">
                      {link.label}
                      <i className={`ri-arrow-down-s-line transition-transform ${
                        (link.label === '보유장비' && mobileEquipmentOpen) ||
                        (link.label === '제조물품' && mobileProductsOpen) ||
                        (link.label === '회사소개' && mobileAboutOpen) ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {((link.label === '보유장비' && mobileEquipmentOpen) ||
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)) && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subMenu?.map((subLink) => (
                          subLink.scrollTo === 'map' ? (
                            <button
                              key={subLink.label}
                              onClick={() => { setMobileMenuOpen(false); handleGoToMap(); }}
                              className="block w-full text-left text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </button>
                          ) : (
                            <Link key={subLink.href} to={subLink.href!} onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50">
                              {subLink.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link key={link.href} to={link.href!} onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                    {link.label}
                  </Link>
                )
              ))}
              <button
                onClick={() => { setMobileMenuOpen(false); setShowContactModal(true); }}
                className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
              >
                고객지원
              </button>
            </div>
          </div>
        </nav>

        {/* 히어로 헤더 */}
        <div className="pt-20 pb-0">
          <div className="relative h-72 md:h-96 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20welding%20workshop%20with%20multiple%20welding%20machines%20MIG%20TIG%20CO2%20laser%20welding%20equipment%20industrial%20manufacturing%20facility%20bright%20clean%20organized%20metal%20fabrication%20factory%20interior%20wide%20angle%20photography&width=1400&height=600&seq=welding-hero-main-v1&orientation=landscape"
              alt="용접장비"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <span className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-2">Welding Equipment</span>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">용접 가공 장비</h1>
              <p className="text-white/80 text-sm md:text-base max-w-2xl">
                MIG·TIG·CO₂·레이저 용접 전 라인 구축으로 다양한 소재와 두께에 최적화된 용접 솔루션을 제공합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 핵심 수치 스탯 */}
        <div className="bg-gray-900 py-8 md:py-12">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: 'ri-flashlight-fill', value: '400A', label: '최대 용접 전류', color: 'bg-orange-500' },
              { icon: 'ri-tools-line', value: '4종', label: '용접 방식', color: 'bg-yellow-500' },
              { icon: 'ri-stack-line', value: '10+', label: '적용 소재', color: 'bg-blue-500' },
              { icon: 'ri-focus-3-line', value: '±0.05mm', label: '레이저 정밀도', color: 'bg-purple-500' },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className={`w-12 h-12 flex items-center justify-center ${stat.color} rounded-full mb-3`}>
                  <i className={`${stat.icon} text-white text-xl`}></i>
                </div>
                <p className="text-white text-2xl md:text-3xl font-bold">{stat.value}</p>
                <p className="text-gray-400 text-xs md:text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 장비 탭 선택 */}
        <div className="py-12 md:py-16 px-5 md:px-20 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Equipment Details</span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">용접 장비 상세</h2>
              <div className="w-10 h-1 bg-orange-500 mx-auto rounded-full"></div>
            </div>

            {/* 탭 버튼 */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {(['mig', 'tig', 'co2', 'laser'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`px-6 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    selectedTab === tab
                      ? 'bg-orange-500 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  {weldingEquipment[tab].name}
                </button>
              ))}
            </div>

            {/* 장비 상세 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* 왼쪽: 장비 이미지 */}
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={currentEquipment.imageUrl}
                  alt={currentEquipment.name}
                  className="w-full h-64 md:h-96 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`${currentEquipment.badge} text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg`}>
                    {currentEquipment.spec}
                  </span>
                </div>
              </div>

              {/* 오른쪽: 장비 사양 */}
              <div className="flex flex-col gap-5">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{currentEquipment.detailTitle}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5">{currentEquipment.detailDesc}</p>
                  
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-orange-500 rounded-full"></span>주요 사양
                  </h4>
                  <div className="space-y-2">
                    {currentEquipment.specTable.map((spec, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <span className="text-xs text-gray-500 font-medium">{spec.label}</span>
                        <span className="text-sm text-gray-900 font-bold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-orange-500 rounded-full"></span>주요 특징
                  </h4>
                  <ul className="space-y-2">
                    {currentEquipment.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-orange-500 mt-0.5 shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* 적용 분야 */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8">
              <h4 className="text-white text-base font-bold mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full">
                  <i className="ri-apps-line text-white text-sm"></i>
                </div>
                주요 적용 분야
              </h4>
              <div className="flex flex-wrap gap-2">
                {currentEquipment.applications.map((app, i) => (
                  <span key={i} className="bg-white/10 text-white text-xs font-medium px-4 py-2 rounded-full border border-white/20">
                    {app}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 소재별 가공 범위 */}
        <div className="py-12 md:py-16 px-5 md:px-20 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Material Range</span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">소재별 용접 가능 범위</h2>
              <div className="w-10 h-1 bg-orange-500 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              {materialRanges.map((material, i) => (
                <div key={i} className={`${material.color} border-2 rounded-2xl p-5 hover:shadow-lg transition-all`}>
                  <div className={`w-12 h-12 ${material.iconBg} rounded-full flex items-center justify-center mb-4`}>
                    <i className={`${material.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{material.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">두께: <strong>{material.thickness}</strong></p>
                  <div className="flex flex-wrap gap-1.5">
                    {material.methods.map((method, j) => (
                      <span key={j} className="bg-white text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full border border-gray-200">
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 용접 공정 흐름 */}
        <div className="py-12 md:py-16 px-5 md:px-20 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Process Flow</span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">용접 가공 공정</h2>
              <div className="w-10 h-1 bg-orange-500 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {processSteps.map((step, i) => (
                <div key={i} className="relative">
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all text-center">
                    <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className={`${step.icon} text-white text-2xl`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-xs text-gray-500">{step.desc}</p>
                  </div>
                  {i < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 translate-x-1/2 z-10">
                      <i className="ri-arrow-right-line text-orange-500 text-xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="py-12 md:py-20 px-5 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-3">
              용접 가공 문의하기
            </h2>
            <p className="text-gray-500 text-sm md:text-base mb-8">
              MIG·TIG·CO₂·레이저 용접 전문가가 최적의 솔루션을 제안해 드립니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowContactModal(true)}
                className="px-8 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-md whitespace-nowrap cursor-pointer text-sm"
              >
                견적 문의하기
              </button>
              <Link
                to="/"
                className="px-8 py-3 bg-white text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors border-2 border-gray-200 whitespace-nowrap cursor-pointer text-sm flex items-center justify-center"
              >
                홈으로 돌아가기
              </Link>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <footer className="bg-orange-500 text-white py-8 px-5">
          <div className="max-w-5xl mx-auto text-center">
            <img
              src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
              alt="NS-TECH Logo"
              className="h-10 w-auto mx-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/80 text-xs mb-1">© 2025 NS-TECH. All rights reserved.</p>
            <p className="text-white/60 text-xs">경기도 포천시 소흘읍 소흘로 11 | Tel: 031-541-1902 | Email: nstech01@naver.com</p>
          </div>
        </footer>

        {/* 모바일 플로팅 전화 버튼 */}
        <a
          href="tel:031-541-1902"
          className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
          aria-label="전화 연결"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-white text-base"></i>
          </div>
          <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
        </a>

        {/* 연락처 모달 */}
        {showContactModal && (
          <div 
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowContactModal(false)}
          >
            <div 
              className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 relative shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="닫기"
              >
                <i className="ri-close-line text-gray-600 text-xl"></i>
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">견적 문의</h3>
                <p className="text-sm text-gray-500">편하신 방법으로 연락주세요</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-building-line text-orange-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">회사명</p>
                    <p className="text-base font-semibold text-gray-900">NS-TECH</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">전화번호</p>
                    <p className="text-base font-semibold text-gray-900">031-541-1902</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-printer-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">팩스</p>
                    <p className="text-base font-semibold text-gray-900">031-541-1903</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">이메일</p>
                    <p className="text-base font-semibold text-gray-900">nstech01@naver.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">운영시간</p>
                    <p className="text-base font-semibold text-gray-900">AM 08:00 ~ PM 18:00</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:031-541-1902"
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  <span>전화하기</span>
                </a>
                <a
                  href="mailto:nstech01@naver.com"
                  className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  <span>메일보내기</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}